import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const AddConsumableModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unit: 'piece',
    current_stock: 0,
    minimum_threshold: 10,
    unit_price: '',
    supplier_id: '',
    reference_code: ''
  });

  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadSuppliers();
    }
  }, [isOpen]);

  const loadSuppliers = async () => {
    try {
      const { data, error } = await supabase?.from('suppliers')?.select('id, name')?.eq('is_active', true)?.order('name');
      if (error) throw error;
      setSuppliers(data || []);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  };

  const unitOptions = [
    { value: 'piece', label: 'Pièce' },
    { value: 'meter', label: 'Mètre' },
    { value: 'liter', label: 'Litre' },
    { value: 'kilogram', label: 'Kilogramme' },
    { value: 'box', label: 'Boîte' },
    { value: 'roll', label: 'Rouleau' },
    { value: 'tube', label: 'Tube' },
    { value: 'cartridge', label: 'Cartouche' },
    { value: 'bottle', label: 'Bouteille' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    onAdd({
      ...formData,
      current_stock: parseInt(formData?.current_stock) || 0,
      minimum_threshold: parseInt(formData?.minimum_threshold) || 10,
      unit_price: formData?.unit_price ? parseFloat(formData?.unit_price) : null
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1020] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="p-6 border-b border-border sticky top-0 bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Package" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Ajouter un consommable</h3>
                <p className="text-sm text-muted-foreground">Remplissez les informations du nouveau consommable</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nom du consommable"
              type="text"
              placeholder="Ex: Joint en caoutchouc 20mm"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              required
            />

            <Input
              label="Code référence"
              type="text"
              placeholder="Ex: JNT-20MM"
              value={formData?.reference_code}
              onChange={(e) => setFormData({ ...formData, reference_code: e?.target?.value })}
            />
          </div>

          <Input
            label="Description"
            type="text"
            placeholder="Description détaillée du consommable"
            value={formData?.description}
            onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Unité de mesure"
              options={unitOptions}
              value={formData?.unit}
              onChange={(value) => setFormData({ ...formData, unit: value })}
              required
            />

            <Input
              label="Stock initial"
              type="number"
              placeholder="0"
              value={formData?.current_stock}
              onChange={(e) => setFormData({ ...formData, current_stock: e?.target?.value })}
              required
            />

            <Input
              label="Seuil minimum"
              type="number"
              placeholder="10"
              value={formData?.minimum_threshold}
              onChange={(e) => setFormData({ ...formData, minimum_threshold: e?.target?.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prix unitaire (€)"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData?.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: e?.target?.value })}
            />

            <Select
              label="Fournisseur"
              options={suppliers?.map((s) => ({ value: s?.id, label: s?.name }))}
              value={formData?.supplier_id}
              onChange={(value) => setFormData({ ...formData, supplier_id: value })}
            />
          </div>

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div className="text-xs text-primary">
                <p className="font-medium mb-1">Information</p>
                <p>Le système créera automatiquement une alerte lorsque le stock descendra en dessous du seuil minimum.</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
              className="flex-1"
            >
              Ajouter
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddConsumableModal;